// src/components/LiveResultsBanner.js
import React, { useEffect, useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, useInView } from 'framer-motion';
import { db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { Link } from 'react-router-dom';

/* ---- Animated counter hook ---- */
function useCountUp(target, duration = 1400) {
  const [value, setValue] = useState(0);
  const prevTarget = useRef(0);

  useEffect(() => {
    if (target == null) return;
    const start = prevTarget.current;
    const end = Number(target);
    const startTime = performance.now();

    const tick = (now) => {
      const elapsed = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - elapsed, 3);
      setValue(start + (end - start) * eased);
      if (elapsed < 1) requestAnimationFrame(tick);
      else { setValue(end); prevTarget.current = end; }
    };
    requestAnimationFrame(tick);
  }, [target, duration]);

  return value;
}

/* ---- Single stat card ---- */
const StatCard = ({ value, label, suffix = '', prefix = '', color, delay, isFloat }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const [triggered, setTriggered] = useState(false);

  useEffect(() => {
    if (inView) setTriggered(true);
  }, [inView]);

  const animated = useCountUp(triggered ? value : 0, 1400);

  const display = isFloat
    ? animated.toFixed(1)
    : Math.round(animated).toLocaleString();

  return (
    <CardWrap
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.55, ease: 'easeOut' }}
    >
      <CardVal color={color}>
        {prefix}{display}{suffix}
      </CardVal>
      <CardLabel>{label}</CardLabel>
    </CardWrap>
  );
};

/* ---- Main component ---- */
const LiveResultsBanner = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'statistics', 'liveResults'), (snap) => {
      if (snap.exists()) setStats(snap.data());
    });
    return () => unsub();
  }, []);

  if (!stats) return null;

  const wins = (stats.tp1Hits || 0) + (stats.tp2Hits || 0);
  const totalResolved = wins + (stats.slHits || 0);
  const winRate = totalResolved > 0 ? Math.round((wins / totalResolved) * 100) : 0;

  return (
    <Section>
      <BgGlow />

      <TopRow>
        <LiveBadge>
          <LiveDot />
          Live Results
        </LiveBadge>
        <TopLabel>Real signals. Real performance. Updated automatically.</TopLabel>
      </TopRow>

      <Headline>
        Proof That AI Signals <GradientSpan>Actually Work</GradientSpan>
      </Headline>
      <SubHeadline>
        These numbers are pulled live from our systems — no backtests, no cherry-picked data.
        Every signal tracked from entry to exit.
      </SubHeadline>

      <StatsGrid>
        <StatCard
          value={stats.growth}
          prefix="+"
          suffix="%"
          label="Portfolio Growth"
          color="#10b981"
          delay={0}
          isFloat
        />
        <StatCard
          value={stats.totalSignals}
          label="Total Signals Fired"
          color="var(--primary-color)"
          delay={0.08}
        />
        <StatCard
          value={stats.tp1Hits}
          label="TP1 Targets Hit"
          color="#10b981"
          delay={0.16}
        />
        <StatCard
          value={stats.tp2Hits}
          label="TP2 Targets Hit"
          color="#10b981"
          delay={0.24}
        />
        <StatCard
          value={winRate}
          suffix="%"
          label="Win Rate"
          color="#F7941D"
          delay={0.32}
        />
        <StatCard
          value={stats.active}
          label="Active Signals Now"
          color="var(--primary-color)"
          delay={0.40}
        />
      </StatsGrid>

      <WinLossRow>
        <WinLossBadge type="win">
          <WinLossIcon>↑</WinLossIcon>
          <WinLossVal>{(stats.tp1Hits || 0) + (stats.tp2Hits || 0)}</WinLossVal>
          <WinLossLabel>Total Wins (TP1 + TP2)</WinLossLabel>
        </WinLossBadge>

        <WinLossVs>vs</WinLossVs>

        <WinLossBadge type="loss">
          <WinLossIcon>↓</WinLossIcon>
          <WinLossVal>{stats.slHits || 0}</WinLossVal>
          <WinLossLabel>Stop Loss Hits</WinLossLabel>
        </WinLossBadge>

        <WinRateBar>
          <WinRateLabel>{winRate}% win rate</WinRateLabel>
          <WinRateTrack>
            <motion.div
              style={{ height: '100%', borderRadius: '999px', background: 'linear-gradient(90deg, #10b981, #34d399)' }}
              initial={{ width: 0 }}
              whileInView={{ width: `${winRate}%` }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, ease: 'easeOut', delay: 0.3 }}
            />
          </WinRateTrack>
        </WinRateBar>
      </WinLossRow>

      <CTARow>
        <DownloadBtn
          href="https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie"
          target="_blank"
          rel="noopener noreferrer"
        >
          Download Free — Get These Signals
        </DownloadBtn>
        <ViewResultsBtn to="/live-results">
          View Full Results &rarr;
        </ViewResultsBtn>
      </CTARow>

      <Disclaimer>
        * Portfolio growth assumes $100 per trade with 10x leverage. Past performance does not guarantee future results.
      </Disclaimer>
    </Section>
  );
};

export default LiveResultsBanner;

/* ============================================================
   KEYFRAMES
============================================================ */
const pulseGlow = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(0.8); }
`;

/* ============================================================
   STYLES
============================================================ */
const Section = styled.section`
  background: var(--bg-main);
  border-top: 1px solid var(--border-color);
  border-bottom: 1px solid var(--border-color);
  padding: 5rem 10%;
  position: relative;
  overflow: hidden;
  text-align: center;

  @media (max-width: 768px) {
    padding: 4rem 1.5rem;
  }
`;

const BgGlow = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(ellipse at 10% 50%, rgba(16,185,129,0.05) 0%, transparent 60%),
    radial-gradient(ellipse at 90% 50%, rgba(41,171,226,0.05) 0%, transparent 60%);
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.2rem;
`;

const LiveBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: rgba(16,185,129,0.1);
  border: 1px solid rgba(16,185,129,0.3);
  color: #10b981;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  padding: 0.35rem 0.9rem;
  border-radius: 999px;
`;

const LiveDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #10b981;
  flex-shrink: 0;
  animation: ${pulseGlow} 1.4s ease-in-out infinite;
`;

const TopLabel = styled.p`
  font-size: 0.85rem;
  color: var(--text-muted);
  margin: 0;
`;

const Headline = styled.h2`
  font-size: 2.8rem;
  font-weight: 800;
  color: var(--text-color);
  line-height: 1.15;
  letter-spacing: -0.5px;
  margin-bottom: 0.8rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const GradientSpan = styled.span`
  background: linear-gradient(135deg, #10b981, #29ABE2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SubHeadline = styled.p`
  font-size: 1rem;
  color: var(--text-secondary-color);
  max-width: 580px;
  margin: 0 auto 3.5rem;
  line-height: 1.75;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 1rem;
  margin-bottom: 2.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const CardWrap = styled(motion.div)`
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  border-radius: 18px;
  padding: 1.5rem 1rem;
  box-shadow: var(--shadow-sm);
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-md);
  }
`;

const CardVal = styled.div`
  font-size: 2rem;
  font-weight: 800;
  color: ${p => p.color || 'var(--text-color)'};
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
  margin-bottom: 0.4rem;

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const CardLabel = styled.div`
  font-size: 0.78rem;
  color: var(--text-muted);
  font-weight: 500;
  line-height: 1.3;
`;

/* Win/Loss visual bar */
const WinLossRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  flex-wrap: wrap;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 20px;
  padding: 1.5rem 2rem;
  margin-bottom: 2.5rem;
  max-width: 760px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 600px) {
    gap: 1rem;
    padding: 1.2rem 1rem;
  }
`;

const WinLossBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${p => p.type === 'win' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.08)'};
  border: 1px solid ${p => p.type === 'win' ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.2)'};
  border-radius: 12px;
  padding: 0.6rem 1rem;
`;

const WinLossIcon = styled.span`
  font-size: 1rem;
  font-weight: 800;
  color: ${p => p.type === 'win' ? '#10b981' : '#ef4444'};
`;

const WinLossVal = styled.span`
  font-size: 1.4rem;
  font-weight: 800;
  color: var(--text-color);
  font-variant-numeric: tabular-nums;
`;

const WinLossLabel = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const WinLossVs = styled.span`
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--text-muted);
`;

const WinRateBar = styled.div`
  flex: 1;
  min-width: 160px;
`;

const WinRateLabel = styled.div`
  font-size: 0.78rem;
  font-weight: 700;
  color: #10b981;
  margin-bottom: 0.4rem;
  text-align: left;
`;

const WinRateTrack = styled.div`
  width: 100%;
  height: 8px;
  background: var(--bg-tertiary);
  border-radius: 999px;
  overflow: hidden;
`;

/* CTA */
const CTARow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.2rem;
`;

const DownloadBtn = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 1rem 2.2rem;
  border-radius: 14px;
  background: linear-gradient(135deg, #29ABE2, #1A5276);
  color: white;
  font-weight: 700;
  font-size: 1rem;
  text-decoration: none;
  box-shadow: var(--shadow-primary);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: var(--shadow-primary-lg);
  }
`;

const ViewResultsBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 1rem 1.8rem;
  border-radius: 14px;
  background: var(--bg-surface);
  color: var(--text-secondary-color);
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  border: 1.5px solid var(--border-color);
  box-shadow: var(--shadow-sm);
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--primary-color);
    color: var(--primary-color);
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }
`;

const Disclaimer = styled.p`
  font-size: 0.72rem;
  color: var(--text-muted);
  max-width: 480px;
  margin: 0 auto;
  line-height: 1.6;
`;
